'use client';

import { ButtonGroupButtonSlot } from '@magicschool/spellbook/components/ButtonGroup/slots';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type ButtonGroupProps = React.ComponentProps<'div'> & VariantProps<typeof buttonGroupVariants>;

export const buttonGroupVariants = cva(['inline-flex', 'group/button-group'], {
  variants: {
    /**
     * The orientation of the button group.
     * @todo SPE-584 — fix doubled border on `vertical` + `display="connected"` with bordered buttons.
     * https://linear.app/magicschool/issue/SPE-584
     */
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
    /** The visual display of the group: connected (dividers, merged borders) or a plain inline row with a standard gap. */
    display: {
      connected: '',
      inline: 'gap-2',
    },
  },
});

const ButtonGroupBase: React.FC<ButtonGroupProps> = ({
  children,
  className,
  orientation = 'horizontal',
  display = 'connected',
  ...props
}) => {
  const { orderedSlots } = useOrderedSlots(children, {
    buttons: { Component: ButtonGroupButtonSlot, props: { orientation, display } },
  });

  return (
    // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for form controls, not action button groups. role="group" is the correct WAI-ARIA pattern for grouping related interactive elements (see https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/)
    <div
      role="group"
      data-spellbook-name="button-group"
      className={cn(buttonGroupVariants({ orientation, display }), className)}
      {...props}
    >
      {mapOrderedSlots(orderedSlots)}
    </div>
  );
};

/**
 * Container for grouping related buttons visually with automatic dividers and connected border styling.
 *
 * ### When to use
 * - For related actions that belong together (e.g., increment/decrement, zoom in/out)
 * - For split button patterns where a primary action has secondary options
 * - For toolbar sections where buttons share visual context
 * - When buttons represent a cohesive set of mutually related actions
 *
 * ### When NOT to use
 * - For unrelated actions: place buttons independently instead
 * - For mutually exclusive choices: use `SegmentedControl` or radio buttons instead
 * - For toggle states: use `ToggleButton` or `ToggleIconButton` instead
 * - For navigation: use `Tabs` or `Link` components instead
 * - For a single button with a dropdown: use `Button` with `DropdownMenu` instead
 * - For a group that needs to collapse into an overflow menu on limited space: use `CollapsibleButtonGroup` instead — it wraps this component and adds that behavior without affecting plain usages
 * - For several groups of controls in one row that should move into an overflow panel as space runs out: use `Toolbar` instead — it measures the row and gives it the ARIA toolbar keyboard model
 *
 * ### API
 * - `orientation`: Layout direction: `horizontal` (default) or `vertical`
 * - `display`: Visual style: `connected` (default — dividers, merged borders) or `inline` (plain row, standard gap, each button keeps its own border radius)
 *
 * ### Composition (slots)
 * - `<ButtonGroup.Button>`: Wrapper for each button in the group. Accepts `Button`, `IconButton`, or interactive wrappers like `Tooltip`/`DropdownMenu` containing buttons. Each button must be wrapped — not rendered by default.
 *
 * ### Visual behavior
 * - **Dividers** (`display="connected"` only): Automatic divider lines appear between buttons (hidden when buttons have `variant="outlined"`)
 * - **Border radius** (`display="connected"` only): First and last buttons keep outer corners rounded; inner corners are squared off for seamless connection
 * - **Hover/Focus**: Individual button hover and focus states are preserved; focused button elevates above siblings
 *
 * ### Accessibility
 * - Renders with `role="group"` to communicate related actions to assistive technology
 * - Each button retains its individual accessibility attributes
 * - Wrap `IconButton` children in `Tooltip` with `mode="label"` to provide accessible names
 *
 * ### Additional notes
 * - Keep groups small (2-4 buttons) for clarity; consider alternative patterns for 5+ actions
 * - Use consistent `size` and `variant` across all buttons in a group for visual harmony
 * - Complex interactions (dropdowns, tooltips) should be placed inside `ButtonGroup.Button` slots and handled at the button level, not the group level
 */
export const ButtonGroup = Object.assign(ButtonGroupBase, {
  Button: ButtonGroupButtonSlot,
});
